import type { DispatchFn } from "@/types/data";
import type { Breadcrumb } from "@/types/view";
import { addBreadCrumbs, setCurrentRoute } from "@/features/currentRouteSlice";
import { setTitle } from "@/helpers/window.helpers";
import { thingsBoardDeepLinkRoutes } from "./navRouteTree";
import { getUrlFromPath } from "./router.helpers";

const routePropertyName = "handleRouteChange";
const breadcrumbPropertyName = "handleBreadcrumbChange";

// ThingsBoard iframe paths that update the parent URL via handleRouteChange.
// Dashboards is in the nav; other entity pages come from thingsBoardDeepLinkRoutes.
export const externallyHandledPaths = [
  "/dashboards",
  ...thingsBoardDeepLinkRoutes.map((route) => route.path),
];

const isExternallyHandledPath = (path: string) =>
  externallyHandledPaths.some((prefix) => path.startsWith(prefix));

/**
 * When the sidebar navigates away from a TB iframe, late Angular NavigationEnd
 * events can still call handleRouteChange and rewrite the parent URL. Suppress
 * until ExternalPage mounts again (or until sync is explicitly resumed).
 *
 * IMPORTANT: ExternalPage components MUST call resumeExternalRouteSync() on mount
 * to re-enable sync after navigation away from TB pages.
 */
let externalRouteSyncSuppressed = false;

export const suppressExternalRouteSync = () => {
  externalRouteSyncSuppressed = true;
};

export const resumeExternalRouteSync = () => {
  externalRouteSyncSuppressed = false;
};

const RESET_EXTERNAL_IFRAME_EVENT = "nimbus:reset-external-iframe";

/**
 * Ask the mounted ExternalPage to reload its iframe to the canonical path
 * without a full parent page refresh (used when RR/URL have desynced via TB).
 */
export const resetExternalIframe = (path: string) => {
  window.dispatchEvent(
    new CustomEvent(RESET_EXTERNAL_IFRAME_EVENT, { detail: { path } })
  );
};

export const subscribeExternalIframeReset = (
  handler: (path: string) => void
) => {
  const listener = (event: Event) => {
    if (!(event instanceof CustomEvent)) return;
    const { path } = event.detail ?? {};
    if (typeof path === "string") handler(path);
  };
  window.addEventListener(RESET_EXTERNAL_IFRAME_EVENT, listener);
  return () =>
    window.removeEventListener(RESET_EXTERNAL_IFRAME_EVENT, listener);
};

/**
 * Initialises a window property for updating the router path.
 * Operation is idempotent.
 */
export const initExternalRouteSync = (dispatch: DispatchFn) => {
  if (routePropertyName in window) return false;

  /**
   * Handles a routing event from an external iframe.
   * Mirrors the TB path onto the parent URL with replaceState (not pushState)
   * so we do not stack a second history entry on top of the iframe navigation.
   *
   * @param path TB-relative path to show in the browser location bar
   * @param pageTitle string to append in the page title
   */
  const handleExternalRouteChange: Window["handleRouteChange"] = (
    path,
    pageTitle
  ) => {
    if (externalRouteSyncSuppressed) return;

    const currentUrlPath = window.location.pathname;
    // remove query params, see PR-2453
    const [cleanPath] = path.split("?");
    const url = getUrlFromPath(cleanPath);

    if (isExternallyHandledPath(cleanPath)) {
      setTitle(pageTitle);
      if (currentUrlPath !== url) {
        window.history.replaceState(null, "", url);
        dispatch(setCurrentRoute(cleanPath));
      }
    }
  };

  Object.defineProperty(window, routePropertyName, {
    value: handleExternalRouteChange,
    writable: false,
  });
  return true;
};

export const initExternalBreadcrumbSync = (dispatch: DispatchFn) => {
  if (breadcrumbPropertyName in window) return false;

  /**
   * Handles a breadcrumb change event from an external iframe.
   *
   * @param breadCrumbs array of breadcrumbs to display in app header
   */
  const handleExternalBreadcrumbChange: Window["handleBreadcrumbChange"] = (
    breadCrumbs: Breadcrumb[]
  ) => {
    dispatch(addBreadCrumbs(breadCrumbs));
  };

  Object.defineProperty(window, breadcrumbPropertyName, {
    value: handleExternalBreadcrumbChange,
    writable: false,
  });
  return true;
};
